import { FormEvent, useContext, useState } from 'react';

import IconButton from '../../../../components/atoms/button/IconButton';
import DragAndDropButton from '../../../../components/atoms/button-drag-drop';
import Input from '../../../../components/atoms/input';
import PDFViewer from '../../../../components/molecules/pdf-viewer/PDFViewer';
import { PdfContentData, PdfContentSpecificData } from '../../../../types/contentTypes';
import AlertContext from '../../../../utils/AlertContext';
import { useFileUpload } from '../../../../utils/hooks';
import { ContentFormProps } from '../ContentEditor';

import './PdfContentForm.scss';

const MAXIMUM_PDF_SIZE = 100;
const ALLOWED_PDF_TYPES = ['pdf'];

export default function PdfContentForm({
  content,
  onNewContentSubmit,
  onExistingContentSubmit,
}: ContentFormProps<PdfContentData, PdfContentSpecificData>) {
  const [headline, setHeadline] = useState<string>(content?.headline || '');

  const showAlert = useContext(AlertContext);

  const [storagePath, setStoragePath] = useState<string>(content?.storagePath || '');
  const [pdfToUpload, setPdfToUpload] = useState<File | 'DELETE_ONLY' | undefined>();

  const { previewUrl, previewFilename, updateStorage } = useFileUpload(
    pdfToUpload,
    content?.storagePath || '',
    [storagePath, setStoragePath],
    'pdfs'
  );

  function handleClearPdfToUpload() {
    setPdfToUpload('DELETE_ONLY');
  }

  async function handleFilePdfSubmit(e: FormEvent) {
    e.preventDefault();
    await updateStorage();
    if (!content) {
      onNewContentSubmit?.({
        storagePath,
        headline,
      });
    } else {
      const { storagePath, headline, ...rest } = content;
      onExistingContentSubmit?.({
        ...rest,
        storagePath,
        headline,
      });
    }
    showAlert('Changes saved!');
  }

  return (
    <form className="video-content-form" onSubmit={handleFilePdfSubmit}>
      <DragAndDropButton
        className={`${previewUrl ? 'mb-2' : 'mb-8'}`}
        setFile={setPdfToUpload}
        maxFileSize={MAXIMUM_PDF_SIZE}
        allowedFileTypes={ALLOWED_PDF_TYPES}
      />
      {previewUrl && (
        <div className="flex items-center mb-8">
          {previewFilename && <p>{previewFilename}</p>}
          <IconButton
            className="text-red"
            icon="close"
            visualType="no-bg"
            iconSize="sm"
            iconColor="red"
            onClick={handleClearPdfToUpload}
          />
        </div>
      )}
      <label className="block mb-4 font-medium">Pdf headline</label>
      <Input className="mb-8 w-full" state={[headline, setHeadline]} type="text" placeholder="Enter pdf headline" />

      {previewUrl && <PDFViewer url={previewUrl} />}
      <IconButton icon="save-outline" label={!content ? 'Save and add to section' : 'Save changes'} type="submit" />
    </form>
  );
}
